<template>
    <LaBaseTable data-test="import-log-table" :headers="headers" :items="data" stripped>
        <template #item-created_at="{ item }">
            <span>{{ formatDateTimePragueTimeZone(item.created_at) }}</span>
        </template>

        <template #item-processed_at="{ item }">
            <span>{{ item.processed_at ? formatDateTimePragueTimeZone(item.processed_at) : "—" }}</span>
        </template>

        <template #item-import_result="{ item }">
            <div v-if="item.import_result" class="import-results">
                <div v-for="(result, name) in item.import_result" :key="name" class="import-result">
                    <LaStatusLabel :type="statusType(result.status)" :label="name" />
                    <div class="import-result-detail">
                        {{ result.status }}
                        <template v-if="result.processed_webs != null">
                            · {{ result.processed_webs }}/{{ result.total_webs }} webs
                        </template>
                        <template v-if="result.since"> · {{ result.since }} → {{ result.to }}</template>
                        <template v-if="result.last_complete_date">
                            · complete to {{ result.last_complete_date }}
                            <template v-if="result.complete_data_age_days != null">
                                ({{ result.complete_data_age_days }}d)
                            </template>
                        </template>
                    </div>
                </div>
            </div>
            <span v-else>—</span>
        </template>

        <template #item-slack_report="{ item }">
            <div v-if="item.slack_report">
                <a class="systems-toggle" data-test="slack-report-toggle" @click="toggleReport(item.id)">
                    {{ Object.keys(item.slack_report.systems || {}).length }} systems
                    <LaBaseIcon :name="isExpanded(item.id) ? 'chevron-up' : 'chevron-down'" />
                </a>
                <div v-if="isExpanded(item.id)" class="systems">
                    <div v-for="(date, system) in item.slack_report.systems" :key="system" class="system">
                        <span class="system-name">{{ system }}</span>
                        <span>{{ date }}</span>
                        <span v-if="isIgnored(item, system)" class="system-ignored">ignored</span>
                    </div>
                </div>
            </div>
            <span v-else>—</span>
        </template>
    </LaBaseTable>
</template>

<script>
import LaBaseTable from "@/components/BaseTable";
import LaBaseIcon from "@/components/BaseIcon";
import LaStatusLabel from "@/components/StatusLabel";
import { formatDateTimePragueTimeZone } from "@/helpers/filters.js";
import { reactive } from "vue";

export default {
    name: "LaImportLogTable",
    components: {
        LaBaseTable,
        LaBaseIcon,
        LaStatusLabel,
    },
    props: {
        data: {
            required: true,
        },
    },
    computed: {
        headers() {
            return [
                {
                    text: "created",
                    value: "created_at",
                    direction: -1,
                },
                {
                    text: "processed",
                    value: "processed_at",
                },
                {
                    text: "type",
                    value: "type",
                },
                {
                    text: "source",
                    value: "source",
                },
                {
                    text: "import result",
                    value: "import_result",
                    flex: "2",
                    sortable: false,
                },
                {
                    text: "slack report",
                    value: "slack_report",
                    sortable: false,
                },
            ];
        },
    },
    setup() {
        const expandedReports = reactive(new Set());

        const toggleReport = (id) => {
            if (expandedReports.has(id)) {
                expandedReports.delete(id);
            } else {
                expandedReports.add(id);
            }
        };
        const isExpanded = (id) => expandedReports.has(id);

        const isIgnored = (item, system) => (item.slack_report.ignored || []).includes(system);

        const statusType = (status) => {
            if (status === "ok") {
                return "success";
            }
            if (status === "triggered") {
                return "info";
            }
            return "error";
        };

        return { formatDateTimePragueTimeZone, toggleReport, isExpanded, isIgnored, statusType };
    },
};
</script>

<style lang="scss" scoped>
.import-results {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    padding: 0.3rem 0;
}
.import-result {
    &-detail {
        font-size: 0.8rem;
        opacity: 0.8;
        padding-left: 0.7rem;
    }
}
.systems-toggle {
    cursor: pointer;
    white-space: nowrap;
}
.systems {
    padding: 0.3rem 0;
    font-size: 0.8rem;
}
.system {
    display: flex;
    gap: 0.5rem;
    &-name {
        min-width: 7rem;
        font-weight: 600;
    }
    &-ignored {
        opacity: 0.6;
        font-style: italic;
    }
}
</style>
